import * as React from 'react';
import { Container, Item } from 'Components/Grid';
import CollapsibleRow from 'Components/CollapsibleRow';
import colors from '../../../../config/theme/colors';
import { styled } from '@mui/system';
import SearchResults from '../../components/SearchResults';
import ExtendedSearchField from './components/ExtendedSearchField';
import SearchSpinner from '../../components/SearchSpinner';
import ActionButtonRounded from 'Components/ActionButtonRounded';
import { CollapsedContent } from './components';
import { useAppDispatch, useAppSelector, useAppSelector as useSelector } from 'Store/index';
import { PlatformSelectionDropdown, PlatformSelectionList } from '../../components/PlatformSelection';
import { actions as sessionActions } from 'Store/session';
import { extendedSearch as extendedSearchFetch } from 'Store/patientsearch/apiFetchers';
import { useIntl } from 'react-intl';
import GroupMessagingCreationDialogs from 'Components/Messaging/components/GroupMessagingCreationDialogs';
import { extendedSearchFields } from './config';
import { TESSearchField, TESSearchFields } from './definitions';

const StyledSubHeader = styled('div')({
  color: colors.primary,
  fontWeight: 600,
  fontSize: '1.8rem',
});

/** For collapsed content - default */
const getCollapsedContentLocPath = (platform: Platform | undefined, name: string) =>
  !(
    (platform === 'ms' && name === 'edss') ||
    (platform === 'parkinson' && ['diagnosis', 'currentHoehnYahr'].includes(name))
  )
    ? 'extendedSearch.opts'
    : undefined;

/** Extended search fields and results */
const ExtendedSearch = ({
  state,
  onChangePlatform,
  onChange,
  onChangeLogOp,
  extendedSearch,
  showSearchResults,
  onClickResult,
  searchStringArray,
  moveToGroupMessages,
}: IOwnProps): React.JSX.Element => {
  const platforms = useSelector(s => s.session.platforms?.available ?? []);
  const userSelectedPlatforms = useSelector(s => s.settings.userSettings.userSelectedPlatforms) ?? platforms;

  const unclearableFields = ['includeDeceased'];

  const handleClear = (name: string): void => {
    const value = state.searchFields[name as keyof typeof state.searchFields];
    if (value) onChange({ [name]: 'noCropping' });
  };

  const searchResults = useSelector((state: IState) => state.patientsearch.searchResults);

  /**
   * These are used to collapse all the search fields when all options are cleared.
   * Takes control of the collapsed prop in CollapsibleRow with collapsed=true and then lets the component do it's own thing again.
   */
  const [collapsed, setCollapsed] = React.useState<true | undefined>(undefined);
  const collapseSearchFields = () => {
    setCollapsed(true);
    setTimeout(() => {
      setCollapsed(undefined);
    }, 100);
  };

  const { formatMessage } = useIntl();
  const fm = (id: string, values?: Record<string, string>) => formatMessage({ id }, values);

  const dispatch = useAppDispatch();

  const [groupMessageSearchLoading, setGroupMessageSearchLoading] = React.useState<boolean>(false);
  const setGroupMessagePatients = () => {
    setGroupMessageSearchLoading(true);
    const searchStringArrayNew = [...searchStringArray];
    if (!searchStringArrayNew.includes('excludeDeceased=true')) {
      searchStringArrayNew.push('excludeDeceased=true');
    }

    const additionalSearchFieldsString = searchStringArrayNew.find(sa => sa.substring(0, 6) === 'search');

    if (additionalSearchFieldsString && !additionalSearchFieldsString.includes('mysqUserStatus')) {
      const mysqQueryString = `§mysqUserStatus€{data.is_mysq_user==TRUE|data.has_delegates==TRUE}`;
      searchStringArrayNew[searchStringArrayNew.indexOf(additionalSearchFieldsString)] =
        additionalSearchFieldsString + '%26' + mysqQueryString;
    }

    const searchString = searchStringArrayNew.join('&');
    void extendedSearchFetch(searchString).then(res => {
      if (res && Array.isArray(res)) {
        setGroupMessageSearchLoading(false);
        dispatch(sessionActions.setMessagesSessionSettings({ groupMessagePatients: res }));
      }
    });
  };

  const messagingOrgSettings = useAppSelector(s => s.settings.orgSettings.messagingSettings);
  const messagingEnabledForPlatform = !!(state.platform && messagingOrgSettings?.[state.platform]);

  return (
    <Container>
      {state.platform && state.platform.length > 0 ? (
        <React.Fragment>
          <Item
            xs={6}
            style={{
              borderRight: `1px solid ${showSearchResults ? colors.gray : 'transparent'}`,
              paddingRight: '4rem',
            }}
          >
            <PlatformSelectionDropdown
              platforms={userSelectedPlatforms}
              selectedPlatform={state.platform}
              onChangePlatform={onChangePlatform}
            />
            <Container style={{ paddingTop: '1.5rem' }}>
              <Item xs={12} style={{ marginBottom: '1.5rem' }}>
                <StyledSubHeader>{fm('extendedSearch.searchCriteria')}</StyledSubHeader>
              </Item>
            </Container>
            <div style={{ marginBottom: '2.5rem' }}>
              {extendedSearchFields(state.searchFields)[state.platform].map((field: TESSearchField, index: number) => {
                const value = state.searchFields[field.name as keyof typeof state.searchFields];

                return field.condition === false ? undefined : (
                  <CollapsibleRow
                    key={index}
                    title={`extendedSearch.${field.title ?? field.name}`}
                    content={
                      <ExtendedSearchField
                        field={field}
                        platform={state.platform}
                        searchFields={state.searchFields}
                        logicalOperators={state.logicalOperators}
                        onChange={onChange}
                        onChangeLogOp={onChangeLogOp}
                      />
                    }
                    collapsedContent={
                      value && value !== 'noCropping' ? (
                        <CollapsedContent
                          name={field.name}
                          value={value}
                          locPath={getCollapsedContentLocPath(state.platform, field.name)}
                        />
                      ) : (
                        fm('extendedSearch.opts.noCropping')
                      )
                    }
                    onClear={
                      unclearableFields.includes(field.name)
                        ? undefined
                        : value !== 'noCropping'
                          ? () => handleClear(field.name)
                          : undefined
                    }
                    /** Collapse the field when onClear used */
                    collapseOnClear
                    /** Collapses the search option field when every search option is cleared with clear button */
                    collapsed={unclearableFields.includes(field.name) ? undefined : collapsed}
                  />
                );
              })}
            </div>
            <div style={{ display: 'flex' }}>
              <div style={{ flex: 1 }}>
                <ActionButtonRounded
                  width={17}
                  height={4}
                  fontSize={16}
                  text="extendedSearch.clearSearchOptions"
                  onClick={() => {
                    collapseSearchFields();
                    window.scrollTo({ behavior: 'smooth', top: 0 });
                    // Reset all fields except platform
                    onChangePlatform({ platform: state.platform });
                  }}
                />
              </div>
              <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                <div>
                  <ActionButtonRounded
                    width={6}
                    height={4}
                    fontSize={16}
                    text="general.search"
                    onClick={() => {
                      window.scrollTo({ behavior: 'smooth', top: 0 });
                      extendedSearch();
                    }}
                    filled
                  />
                </div>
              </div>
            </div>
          </Item>
          <Item xs={6} style={{ paddingLeft: '4rem' }}>
            {showSearchResults &&
              (state.searchLoading ? (
                <SearchSpinner text="general.extendedSearching" style={{ height: 'auto' }} />
              ) : (
                <div>
                  <SearchResults searchResults={searchResults ?? []} onClickResult={onClickResult} />
                  {(searchResults ?? []).length > 0 &&
                  messagingEnabledForPlatform &&
                  state.searchFields.myServiceInUse !== 'no' ? (
                    <Container justifyContent="flex-end" style={{ marginTop: '1rem' }}>
                      <Item>
                        <ActionButtonRounded
                          text="messaging.newGroupMessage"
                          width="auto"
                          height={3}
                          fontSize={14}
                          onClick={setGroupMessagePatients}
                          loading={groupMessageSearchLoading}
                          tooltip={<>{fm('messaging.groupMessageTooltip')}</>}
                          disabledTooltip={<>{fm('messaging.groupMessageTooltip')}</>}
                          disabled={(searchResults ?? []).length < 2}
                        />
                      </Item>
                    </Container>
                  ) : null}
                </div>
              ))}
          </Item>
        </React.Fragment>
      ) : (
        <Item>
          <PlatformSelectionList platforms={userSelectedPlatforms} onChangePlatform={onChangePlatform} />
        </Item>
      )}
      <GroupMessagingCreationDialogs selectedPlatform={state.platform} moveToGroupMessages={moveToGroupMessages} />
    </Container>
  );
};

export interface IExtendedSearch {
  platform?: Platform;
  searchFields: TESSearchFields;
  searchLoading: boolean;
  logicalOperators: Partial<Record<keyof TESSearchFields, 'AND' | 'OR'>>;
}

interface IOwnProps {
  state: IExtendedSearch;
  onChangePlatform: (e: TOnChangeValues) => void;
  onChange: (e: TOnChangeValues) => void;
  onChangeLogOp: (e: TOnChangeValues) => void;
  extendedSearch: () => void;
  showSearchResults: boolean;
  onClickResult: (id?: string) => void;
  searchStringArray: string[];
  moveToGroupMessages?: () => void;
}

export default ExtendedSearch;
